// Plan 08-04 — PlayerView TypeScript shape mirroring backend's wire contract.
//
// Source of truth: apps/backend/src/match/view/player-view.ts `PlayerView` type
// (DEC-021 B12 firewall — server projects MatchInternalState → PlayerView for
// the requesting slot only). The backend WebSocket codec
// (`apps/backend/src/match/ws/protocol.ts encodeTurnApplied / encodeStateSnapshot`)
// JSON.stringify's the FULL PlayerView into the protobuf `your_state_json`
// field; the protobuf wraps it in an Envelope keyed by MessageKind.
//
// IMPORTANT — this mirror is the runtime-decode target:
//   1. The web codec calls `decodeFrame(bytes)` → `runegambit.v1.IEnvelope`
//   2. For STATE_SNAPSHOT / TURN_APPLIED: read `env.{state_snapshot,turnApplied}.yourState.yourStateJson`
//   3. JSON.parse() that string → `PlayerView` (this shape)
//
// This shape is DIFFERENT from the protobuf `runegambit.v1.PlayerView` message,
// which only carries 5 top-level fields (currentTurnIdx, currentPlayerSlot,
// status, yourSlot, yourStateJson). The full structured shape lives inside
// `yourStateJson` as JSON.
//
// B12 firewall (NY-08-10): the wire NEVER contains opponent private state. The
// backend's `projectPlayerView` strips deck_seed, opponent rune-rack contents,
// opponent words_played, unrevealed shared cells. The client renders ONLY from
// this shape — no inference, no slot injection.
//
// P34-A12 audit-conflict resolution (2026-05-17): this file is verified-clean
// of pre-Runespring PlayerView fields (caller-only letter rack + visible-cell
// mirror — both DROPped at migration 0016). The backend audit's flag was a
// false positive; the web audit's verdict stands. Forbidden tokens are not
// named here to avoid tripping the RUNE-36 cleanup scanner.

export type PowerUpName =
  | 'scriveners_quill'
  | 'tongue_tie'
  | 'lexical_mulligan'
  | 'knights_vow'
  | 'castle_keep'
  | 'anagram_sigil'
  | 'the_vanishing'
  | 'whisperers_promise'

export type RoleKind = 'king' | 'queen' | 'rook' | 'bishop' | 'knight'

export type Language = 'en' | 'sv'

export interface MatchConfig {
  language: Language
  minWordLength: number
  // 20 / 30 / 40 / 0 (0 = unlimited). DEC-023 host-configurable.
  lengthCap: number
  turnClockMs: number
  // length 4 per DEC-024 (planner intent: 8 names exist, loadout is 4 selected).
  powerupLoadout: ReadonlyArray<PowerUpName>
  dictionary_version: string
}

/**
 * Backend wire status string. Mirrors `MatchInternalState['status']`.
 *
 * Wire terminal statuses emitted by the current backend:
 *   `completed | abandoned | timeout_resolved | stalemate_resolved`
 * The true per-player outcome is derived from `winner_slot` (CINE-1).
 *
 * `won` / `lost` / `drawn` are retained in this union for fixture /
 * back-compat parity only — they are NOT emitted by the current backend.
 * `deriveTerminalOutcome` handles them as a legacy fast-path.
 */
export type MatchStatus =
  | 'lobby'
  | 'active'
  | 'completed'
  | 'won'
  | 'lost'
  | 'drawn'
  | 'abandoned'
  | 'timeout_resolved'
  | 'stalemate_resolved'

/**
 * The full PlayerView shape — the JSON-stringified payload sitting inside
 * the protobuf `your_state_json` field. Mirrors
 * apps/backend/src/match/view/player-view.ts `PlayerView` byte-for-byte.
 *
 * NOTE: backend uses snake_case for most fields and camelCase for `matchId`
 * (a single oddity inherited from the OpenAPI v1 contract). The mirror
 * preserves that mix verbatim — drift would break wire parity.
 */
export interface PlayerView {
  matchId: string
  your_slot: 0 | 1
  current_turn_idx: number
  current_player_slot: 0 | 1
  your_tokens: number
  your_role: RoleKind | null
  your_role_expires_turn: number | null
  your_words_played: ReadonlyArray<string>
  /**
   * ARCA-152 — caller's running cumulative word score (Σ per-turn wordScore).
   * Mirrors the backend's caller-only `PlayerView.your_score` — the opponent's
   * running score is NEVER on the wire mid-match (B12); both finals arrive at
   * match-end via the `_score_reveal` sidecar ($scoreReveal). Optional here for
   * the same predates-this-field reason as `available_power_ups`: consumers
   * default to 0 when undefined.
   */
  your_score?: number
  your_powerup_loadout: ReadonlyArray<PowerUpName>
  your_powerups_used: Readonly<Record<string, number>>
  // WR-02 fix (Phase 14 fix-pass iter 1): server-filtered available power-
  // ups list. Mirrors apps/backend/src/match/view/player-view.ts:340
  // (`available_power_ups: computeAvailablePowerUps(state, slot)`). The
  // PowerupTray re-reads this on every render to disable cards whose
  // power-up is no longer available (Pitfall 7 — never cache locally).
  // Optional in the type because in-flight matches whose wire payload
  // predates Phase 13 PWR-01 may omit the field; consumers default to []
  // when undefined (server authority — DEC-021).
  available_power_ups?: ReadonlyArray<PowerUpName>
  // WR-02 sidekick — Phase 13 PWR-02 caller's Scrivener reveals. The
  // backend's projectPlayerView emits this field; declaring it here
  // keeps the schema honest about what the wire delivers. Optional for
  // the same predates-PWR-02 reason as available_power_ups.
  your_revealed_opponent_letters?: ReadonlyArray<{
    cell_idx: number
    letter: string
  }>
  opponent_public: {
    captures: number
    powerup_loadout: ReadonlyArray<PowerUpName>
    role_kind: RoleKind | null
    role_expires_turn: number | null
  }
  /**
   * Unified board cell shape — mirrors the wire projection in
   * apps/backend/src/match/view/player-view.ts (`board: state.board.map(...)`).
   * The chess and word games share ONE 6-column board (GDD §5.1: a single
   * board with two interchangeable views), so every cell carries BOTH the
   * Runespring letter surface AND the chess-piece surface. LetterLayer reads
   * the letter fields; PieceLayer reads the piece fields off the same array.
   *
   * Letter surface (Runespring, Phases 23-26):
   *   - letter:        null when is_hollow=true; otherwise the rune glyph.
   *   - tier:          'common' | 'forged' | 'rare' | 'mythic' — drives tier
   *                    embellishment overlay (forged ring / rare inlay /
   *                    mythic glow + sparkle).
   *   - tint:          'a' | 'b' | null — owner-tint applied at α=0.25
   *                    (standard) / α=1.0 (Anchor). null only when is_hollow.
   *   - is_anchor:     true → render anchor_sigil_rune at top-right and
   *                    promote tint α to 1.0.
   *   - is_hollow:     true → swap letter/tile for hollow_cracked_rune_stone;
   *                    breaks word-trace adjacency.
   *   - is_frozen:     true → cell is frozen by Tongue-Tie (ARCA-142); neither
   *                    player can trace through it until the freeze decays.
   *                    WordTraceLayer rejects it at trace time (4-channel
   *                    compound) and MatchGrid renders a distinct icy
   *                    treatment. OPTIONAL in this mirror (see note below).
   *   - anchor_owner:  0 | 1 | null — slot that placed the anchor (null on
   *                    non-anchor cells); drives sigil color via
   *                    --rune-anchor-sigil-color-player-{a,b}.
   *
   * Chess-piece fields (restored — fix/restore-chess-pieces). Runespring
   * (cefaf82) dropped these from the client mirror even though the backend's
   * projectPlayerView still emits them on every board cell
   * (apps/backend/src/match/view/player-view.ts board map). PieceLayer /
   * PieceDragLayer / MatchGrid read these directly. All six are required —
   * the backend emits them unconditionally for every cell (per-cell role
   * visibility is fully public per DEC-023):
   *
   *   - piece_owner_slot:      0 | 1 | null — slot owning the piece on the
   *                            cell (null on empty cells).
   *   - piece_kind:            'blank' | 'king' | null — piece glyph (null on
   *                            empty cells).
   *   - role_kind:             RoleKind | null — empowered role for the piece.
   *   - role_expires_turn:     number | null — turn the role lapses.
   *   - role_extensions_used:  number — PWR-05 per-piece extension counter.
   *   - can_extend:            boolean — server-computed extend eligibility.
   */
  board: ReadonlyArray<{
    cell_idx: number
    letter: string | null
    tier: 'common' | 'forged' | 'rare' | 'mythic'
    tint: 'a' | 'b' | null
    is_anchor: boolean
    is_hollow: boolean
    // ARCA-142 — Tongue-Tie freeze flag. The backend's projectPlayerView
    // ALWAYS emits this (derived from active `freeze_shared_cell` effects),
    // but it is OPTIONAL in this mirror for the same reason as
    // `available_power_ups?`: in-flight matches whose wire payload predates
    // this field omit it, and the ~29 PlayerView fixtures that build partial
    // boards need not all be churned. Consumers MUST treat a missing value as
    // `false` (`cell.is_frozen === true` / `cell.is_frozen ? … : undefined`) —
    // the server is authoritative (DEC-021).
    is_frozen?: boolean
    anchor_owner: 0 | 1 | null
    piece_owner_slot: 0 | 1 | null
    piece_kind: 'blank' | 'king' | null
    role_kind: RoleKind | null
    role_expires_turn: number | null
    role_extensions_used: number
    can_extend: boolean
  }>

  /**
   * ARCA-749 (ARCA-717 WS1/WS3) — the decoupled wire grids (Option 2). `board`
   * above is the TRANSITIONAL combined projection (pieces overlaid on word cells
   * 0..35); these two grids are the contract the split renderer consumes. Mirror
   * of the backend `PlayerView.chess_grid` / `word_field`
   * (apps/backend/src/match/view/player-view.ts), sliced there from the same
   * combined board via `splitWireGrids` so the two can never drift.
   *
   * `chess_grid` — the FIXED 6×6 chess board: exactly 36 cells, piece + role
   * state only. NEVER grows or shifts on a Flop/River reveal. PieceLayer renders
   * pieces off this grid; the chess view is therefore always 6×6.
   *
   * Optional here for fixture/transition resilience (same rationale as
   * `your_score?` / `is_frozen?`): the live backend ALWAYS emits both grids, but
   * the ~30 partial-board PlayerView fixtures predate them. Consumers fall back
   * to deriving the split from `board` via `game/wire-grids.ts`
   * (`resolveChessGrid` / `resolveWordField`) when undefined — the client mirror
   * of the backend slice.
   */
  chess_grid?: ReadonlyArray<{
    cell_idx: number
    piece_owner_slot: 0 | 1 | null
    piece_kind: 'blank' | 'king' | null
    role_kind: RoleKind | null
    role_expires_turn: number | null
    role_extensions_used: number
    can_extend: boolean
  }>

  /**
   * `word_field` — the word/Runespring field: word surface only (letter + rarity
   * + anchor/hollow/freeze). Grows independently of the chess grid via Flop/River
   * (36 → 48 → 60 cells). No piece columns. LetterLayer renders cubes off this
   * grid; anchor placement (AnchorPlacementOverlay) reads it too — and since it
   * carries no piece columns, the ARCA-599 anchor-on-piece exclusion is moot here
   * (anchors place freely on the word grid; the chess grid is a separate surface).
   * Optional for the same fixture-resilience reason as `chess_grid` above.
   */
  word_field?: ReadonlyArray<{
    cell_idx: number
    tier: 'common' | 'forged' | 'rare' | 'mythic'
    tint: 'a' | 'b' | null
    is_anchor: boolean
    anchor_owner: 0 | 1 | null
    is_hollow: boolean
    is_frozen: boolean
    letter: string | null
  }>

  /**
   * Public role-purchase terms (cost + minimum word length per empowerable
   * role), projected by the backend from its live-ops balance config
   * (apps/backend/src/match/view/player-view.ts `role_costs`). Surfaced for
   * DISPLAY only — the EmpowerModal renders these so it never hardcodes costs.
   * The server stays authoritative: `validateEmpower` re-derives the charged
   * cost from the same config and cross-checks the word length, so a tampered
   * client cannot spoof a cheaper role. Keys are the four empowerable roles
   * (king is the start-piece — never purchasable, so absent here).
   */
  role_costs: Record<
    'knight' | 'bishop' | 'rook' | 'queen',
    { cost: number; min_word_length: number }
  >

  /**
   * ARCA-591 — public role-EXTENSION (re-empower) terms, projected by the
   * backend from its live-ops balance config (apps/backend/src/match/view/
   * player-view.ts `extension_terms`). `token_cost` is the flat per-extension
   * token cost (1 per DEC-023); `turns` is the lifetime bump per extension
   * (`balance.extensionTurns`, default 3). DISPLAY only — the ExtendModal
   * labels the re-empower affordance from these so it never hardcodes the
   * terms; the server stays authoritative (`validateExtend` re-derives the
   * cost). Optional here for the same predates-this-field resilience reason as
   * `your_score?` / `available_power_ups?`: in-flight matches whose wire
   * payload was projected before this field omit it; consumers fall back to
   * the DEC-023 defaults `{ token_cost: 1, turns: 3 }` when undefined.
   */
  extension_terms?: { token_cost: number; turns: number }
  config: MatchConfig

  /**
   * Phase 27 (RUNE-21) / Phase 31 — caller's secret 4-letter affinity tuple.
   * B12 firewall: the opponent's PlayerView NEVER includes this field — the
   * backend's `projectPlayerView` only emits it on the caller's projection
   * (apps/backend/src/match/view/player-view.ts). Revealed publicly only at
   * match end via the terminal `affinity_reveal: { a, b }` sidecar (which is
   * NOT a PlayerView field — surfaced via the TURN_APPLIED final frame). The
   * length-4 tuple type is the type-construction enforcement of DEC-032.
   */
  your_affinity_letters: readonly [string, string, string, string]

  /**
   * Phase 28 (RUNE-24c) / Phase 31 — caller-only Quill Read reveals. Empty
   * tuple until the caller plays Quill in Read mode; populated with the
   * opponent's 2 letters once revealed. B12 firewall: opponent's PlayerView
   * never carries this field (caller-only by type construction at
   * `projectPlayerView`). Visualized by KnownOpponentRunesRow (Plan 31-04).
   */
  your_read_revealed_letters: readonly [string, string] | readonly []

  /**
   * Phase 28 (RUNE-24a) / Phase 31 — Mark queued flag. Server omits the field
   * when no Mark is pending (omit-when-false is the B12 invariant — emitting
   * `false` would leak the *absence* of a queued Mark, which is itself a side-
   * channel). Renders the Quill-Mark queued indicator when present.
   */
  your_pending_mark?: true

  /**
   * Phase 26 (RUNE-15) / Phase 31 — server-set Anchor placement gate. True at
   * the start of T2/T3/T8/T9 for the active player; the client MUST render
   * the AnchorPlacementOverlay (Plan 31-03) and POST /api/match/{id}/place-
   * anchor before Submit/Skip become usable. Server clears the flag atomically
   * with a successful place-anchor response (next PlayerView frame).
   */
  requires_anchor_placement: boolean

  /**
   * Plan 1-2 (two-step turn) — which half of the turn the active player is
   * currently in. 'word' = Phase-A (word) step pending; 'chess' = Phase-A
   * already submitted this turn, Phase-B (chess) step pending. Mirrors the
   * backend's `PlayerView.turn_phase` (projectPlayerView always emits it).
   * Drives MatchScreen's two-step submit flow (submitWord then submitChess).
   */
  turn_phase: 'word' | 'chess'

  /**
   * Two-step-turn empower support — CALLER-ONLY length of the committed
   * Phase-A word, surfaced by the backend ONLY when `turn_phase === 'chess'`
   * AND the caller is the active player AND a word (not skip/pass) was
   * committed. The word step clears the client's in-memory phaseA, so this is
   * the only source of the committed length during the chess step: the
   * EmpowerModal gates its role buttons on it (`wordLength` prop) and
   * onEmpowerConfirm sends it as `source_word_length` (validated server-side
   * against the committed word). OMITTED otherwise (word step, no committed
   * word, opponent view) — consumers default to 0 when undefined. Mirrors the
   * backend's optional `your_pending_word_length` (projectPlayerView).
   */
  your_pending_word_length?: number

  /**
   * Phase 26 (RUNE-20) / Phase 31 — one-shot lifetime tutorial signal. Server
   * sets `true` EXACTLY ONCE for the caller's first lifetime Anchor
   * consumption, then OMITS the field forever on subsequent PlayerView
   * projections. The client mounts FirstAnchorTutorialModal on observing
   * `=== true`; dismissal is purely visual (server-side flag is already
   * consumed atomically with the consumption side-effect). NO local-storage
   * fallback — cross-device coherent.
   */
  tutorial_anchor_consumption_first_time?: boolean

  /**
   * Piece-move legality hints — caller-only map from each of the caller's own
   * piece cell_idxs to its server-computed legal destination cell_idxs. The
   * backend's `projectPlayerView` derives this from `legalDestinations`
   * (apps/backend/src/match/validation/chess.ts), so ALL movement rules stay
   * server-authoritative (no rules ship to clients → no cross-language
   * divergence, offline-compatible). Cells with no own piece are absent; a
   * piece with no legal move maps to an empty array. The chess-view drag/
   * keyboard FSM gates drops on this map.
   */
  your_legal_moves: Readonly<Record<number, ReadonlyArray<number>>>

  /**
   * CINE-1 Task A (winner_slot wire field) — projected by the backend on every
   * PlayerView frame. Null during active play; 0|1|null at terminal (null = true
   * draw / stalemate, 0|1 = the winning slot). Both slots receive the same value
   * (not B12-firewalled). Optional here for fixture/transition resilience —
   * `deriveTerminalOutcome` treats an absent field as 'complete' (defensive).
   */
  winner_slot?: 0 | 1 | null
}

/**
 * Lighter shape returned by GET /api/match/{id}/state on the polling fallback
 * path (Phase 4 ETag polling). Same surface as PlayerView for v1 — the
 * polling fallback simply re-projects through the same `projectPlayerView`
 * function. Defined here so Plan 05 / Plan 09 fetch wrapper has a typed
 * return slot; structurally identical to PlayerView for now.
 */
export type PlayerViewLite = PlayerView

/**
 * Best-effort runtime guard for the parsed-JSON inner shape. Defensive only —
 * the canonical authority is the server's `projectPlayerView` (B12 firewall
 * is enforced server-side; this guard catches obvious tampering before it
 * reaches the renderer).
 *
 * Strategy: structural type-check on required scalars + length sanity. We do
 * NOT deep-validate every field — that would require a 200-line schema, and
 * the server's projection is the single source of truth.
 */
export function isPlayerView(x: unknown): x is PlayerView {
  if (typeof x !== 'object' || x === null) return false
  const o = x as Record<string, unknown>
  if (typeof o.matchId !== 'string') return false
  if (o.your_slot !== 0 && o.your_slot !== 1) return false
  if (typeof o.current_turn_idx !== 'number') return false
  if (o.current_player_slot !== 0 && o.current_player_slot !== 1) return false
  if (typeof o.your_tokens !== 'number') return false
  if (!Array.isArray(o.your_words_played)) return false
  if (!Array.isArray(o.your_powerup_loadout)) return false
  if (typeof o.your_powerups_used !== 'object' || o.your_powerups_used === null)
    return false
  if (typeof o.opponent_public !== 'object' || o.opponent_public === null)
    return false
  if (!Array.isArray(o.board)) return false
  // fix/restore-chess-pieces — defensive per-cell check for the restored
  // chess-piece fields. The backend emits all six on every board cell; the
  // guard rejects a cell missing them (Runespring-era wire / tampering).
  for (const cell of o.board as unknown[]) {
    if (typeof cell !== 'object' || cell === null) return false
    const c = cell as Record<string, unknown>
    if (
      c.piece_owner_slot !== 0 &&
      c.piece_owner_slot !== 1 &&
      c.piece_owner_slot !== null
    )
      return false
    if (
      c.piece_kind !== 'blank' &&
      c.piece_kind !== 'king' &&
      c.piece_kind !== null
    )
      return false
    if (!('role_kind' in c)) return false
    if (!('role_expires_turn' in c)) return false
    if (typeof c.role_extensions_used !== 'number') return false
    if (typeof c.can_extend !== 'boolean') return false
  }
  // ARCA-749 — the decoupled split grids are optional on the wire-decoded JSON
  // (live backend always emits them; partial fixtures may omit them, in which
  // case consumers derive them from `board`). Validate ONLY when present: the
  // chess grid is the FIXED 6×6 (exactly 36 cells), the word field carries the
  // word surface (length tracks the growing board). Defensive — the server's
  // projection is the canonical authority.
  if ('chess_grid' in o && o.chess_grid !== undefined) {
    if (!Array.isArray(o.chess_grid) || o.chess_grid.length !== 36) return false
  }
  if ('word_field' in o && o.word_field !== undefined) {
    if (!Array.isArray(o.word_field)) return false
  }
  if (typeof o.role_costs !== 'object' || o.role_costs === null) return false
  if (typeof o.config !== 'object' || o.config === null) return false
  // Phase 31 — defensive checks on the 5 new caller-only fields. Server-side
  // projection is the canonical authority; these checks catch tampering on
  // the wire-decoded JSON before it reaches the renderer.
  if (
    !Array.isArray(o.your_affinity_letters) ||
    o.your_affinity_letters.length !== 4
  )
    return false
  if (!Array.isArray(o.your_read_revealed_letters)) return false
  if (
    o.your_read_revealed_letters.length !== 0 &&
    o.your_read_revealed_letters.length !== 2
  )
    return false
  if (typeof o.requires_anchor_placement !== 'boolean') return false
  if (o.turn_phase !== 'word' && o.turn_phase !== 'chess') return false
  if ('your_pending_mark' in o && o.your_pending_mark !== true) return false
  if (
    'your_pending_word_length' in o &&
    typeof o.your_pending_word_length !== 'number'
  )
    return false
  if (
    'tutorial_anchor_consumption_first_time' in o &&
    typeof o.tutorial_anchor_consumption_first_time !== 'boolean'
  )
    return false
  if (typeof o.your_legal_moves !== 'object' || o.your_legal_moves === null)
    return false
  // CINE-1 Task B — winner_slot is optional; validate only when present.
  if (
    'winner_slot' in o &&
    o.winner_slot !== 0 &&
    o.winner_slot !== 1 &&
    o.winner_slot !== null
  )
    return false
  return true
}
